import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CircleDollarSign, Loader2, Search } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const ESTATUS_STYLES = {
  "Autorizada": "bg-emerald-100 text-emerald-700",
  "Vigente": "bg-sky-100 text-sky-700",
  "Suspendida": "bg-amber-100 text-amber-700",
  "Vencida": "bg-slate-200 text-slate-700",
  "Cancelada": "bg-rose-100 text-rose-700",
};

function EstatusBadge({ estatus }) {
  const cls = ESTATUS_STYLES[estatus] || "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`} data-testid={`linea-estatus-${estatus}`}>
      {estatus || "—"}
    </span>
  );
}

const fmtDinero = (n) =>
  typeof n === "number"
    ? n.toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 })
    : "—";

const fmtFecha = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return iso;
  }
};

export default function Lineas() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [estatus, setEstatus] = useState("todos");
  const [loading, setLoading] = useState(true);
  const [errorAcceso, setErrorAcceso] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lineas");
      setRows(Array.isArray(data) ? data : []);
      setErrorAcceso("");
    } catch (e) {
      setErrorAcceso(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filas = rows
    .filter((r) => estatus === "todos" || r.estatus === estatus)
    .filter((r) => {
      const s = q.trim().toLowerCase();
      if (!s) return true;
      return (
        String(r.numero_linea || "").toLowerCase().includes(s) ||
        String(r.cliente || "").toLowerCase().includes(s) ||
        String(r.producto || "").toLowerCase().includes(s)
      );
    });

  const stats = {
    total: rows.length,
    activas: rows.filter((r) => ["Autorizada", "Vigente"].includes(r.estatus)).length,
    suspendidas: rows.filter((r) => r.estatus === "Suspendida").length,
    limite_total: rows
      .filter((r) => ["Autorizada", "Vigente", "Suspendida"].includes(r.estatus))
      .reduce((s, r) => s + Number(r.limite_autorizado || 0), 0),
    disponible_total: rows
      .filter((r) => ["Autorizada", "Vigente"].includes(r.estatus))
      .reduce((s, r) => s + Number(r.disponible || 0), 0),
  };

  const cols = [
    { key: "numero_linea", header: "Línea", render: (r) => <span className="font-mono text-sm">{r.numero_linea}</span> },
    { key: "cliente", header: "Cliente" },
    { key: "producto", header: "Producto" },
    { key: "limite_autorizado", header: "Límite", render: (r) => <span className="font-semibold">{fmtDinero(Number(r.limite_autorizado))}</span> },
    { key: "dispuesto", header: "Dispuesto", render: (r) => fmtDinero(Number(r.dispuesto)) },
    { key: "disponible", header: "Disponible", render: (r) => <span className="text-emerald-700 font-medium">{fmtDinero(Number(r.disponible))}</span> },
    { key: "fecha_vencimiento", header: "Vence", render: (r) => fmtFecha(r.fecha_vencimiento) },
    { key: "estatus", header: "Estatus", render: (r) => <EstatusBadge estatus={r.estatus} /> },
  ];

  if (errorAcceso) {
    return (
      <div className="p-6" data-testid="lineas-error-acceso">
        <PageHeader title="Líneas de crédito" description="Instrumentos padre de las disposiciones revolventes." icon={CircleDollarSign} />
        <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-800">{errorAcceso}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="lineas-page">
      <PageHeader
        title="Líneas de crédito"
        description="Instrumentos padre revolventes. Cada línea aprueba un límite; las disposiciones (Fase 3) generan los créditos hijos."
        icon={CircleDollarSign}
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <StatCard label="Líneas totales" value={stats.total} testid="stat-lineas-total" />
        <StatCard label="Activas" value={stats.activas} testid="stat-lineas-activas" />
        <StatCard label="Límite total" value={fmtDinero(stats.limite_total)} testid="stat-lineas-limite" />
        <StatCard label="Disponible total" value={fmtDinero(stats.disponible_total)} testid="stat-lineas-disponible" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar por número, cliente o producto"
            className="w-80 pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="lineas-buscar"
          />
        </div>
        <Select value={estatus} onValueChange={setEstatus}>
          <SelectTrigger className="w-48" data-testid="lineas-filtro-estatus"><SelectValue placeholder="Estatus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estatus</SelectItem>
            <SelectItem value="Autorizada">Autorizada</SelectItem>
            <SelectItem value="Vigente">Vigente</SelectItem>
            <SelectItem value="Suspendida">Suspendida</SelectItem>
            <SelectItem value="Vencida">Vencida</SelectItem>
            <SelectItem value="Cancelada">Cancelada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500" data-testid="lineas-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando líneas…
        </div>
      ) : (
        <DataTable
          columns={cols}
          rows={filas}
          onRowClick={(r) => navigate(`/lineas/${r.id}`)}
          empty="No hay líneas de crédito. Cuando autorices una solicitud de producto revolvente, aparecerá aquí."
          testid="tabla-lineas"
        />
      )}
    </div>
  );
}
